import React, { useMemo } from 'react';

import { useGetFace, useGetLists } from '@vlabs/api/artemy/client';
import type { FaceId, OutputFace } from '@vlabs/api/artemy/model';
import { ZoomInIcon } from '@vlabs/icons';
import { DeleteFaceButtonPoint3 } from '@vlabs/monokit/features/delete-face/DeleteFaceButton.point3';
import { EditFaceInfoButtonPoint3 } from '@vlabs/monokit/features/edit-face-info';
import { SearchSimilarFacesButtonPoint3 } from '@vlabs/monokit/features/search-similar-faces/SearchSimilarFacesButton.point3';
import { useFullscreenImageViewer } from '@vlabs/monokit/features/view-photo';
import { Link } from '@vlabs/monokit/shared/ui/link';
import { useHasPermission, usePageAccess } from '@vlabs/permissions';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { parseISOToLocale } from '@vlabs/shared/utils';
import {
  ButtonScrimGroup, CopyButton, FaceSample, Tooltip,
} from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import { routes } from '@vlapps/point/src/app/routes';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import st from './FaceGeneralInfo.module.sass';
import ListSeparator from './ListSeparator.svg?react';

const fmt = (value: React.ReactNode): React.ReactNode => {
  if (typeof value !== 'string' && !!value) return value;
  if (typeof value === 'string' && value.length > 0) return value;

  return <span className={st.DataValue_text}>-</span>;
};

interface FaceGeneralInfoProps {
  faceId: FaceId;
}

function FaceGeneralInfo({
  faceId,
}: FaceGeneralInfoProps): React.ReactNode {
  const { t } = useTranslation();
  const canSearchFace = usePageAccess('search');
  const canUpdateCard = useHasPermission('card', 'update');
  const canDeleteCard = useHasPermission('card', 'delete');
  const canViewCardDetails = usePageAccess('cards');
  const canReadLists = useHasPermission('list', 'read');
  const getFace = useGetFace(faceId);
  const getLists = useGetLists({ list_ids: getFace.data?.lists }, {
    query: {
      enabled: (getFace.data?.lists?.length ?? 0) > 0,
    },
  });

  const avatarSrc = getFace.data?.avatar ? `/api/${getFace.data?.avatar}` : undefined;

  const fullscreenImageViewer = useFullscreenImageViewer({ url: avatarSrc });

  const externalIdLink = useMemo(() => {
    if (!getFace.data?.card_id) return undefined;
    return (
      <Link
        data-testid={getFace.data.card_id}
        disabled={!canViewCardDetails}
        to={generatePath(routes.externalId.read, { externalId: getFace.data.card_id })}
        type="secondary"
      >
        {getFace.data.card_id}
      </Link>
    );
  }, [canViewCardDetails, getFace.data?.card_id]);

  const lists = useMemo(() => {
    if ((getLists.data?.lists?.length ?? 0) === 0) return undefined;
    return (
      <div className={st.Lists}>
        {getLists.data?.lists?.map((list, idx) => {
          return (
            <React.Fragment key={list.list_id}>
              <Link
                data-testid={list.list_id}
                size="large"
                to={generatePath(routes.lists.read, { listId: list.list_id })}
                type="secondary"
              >
                {list.user_data}
              </Link>
              {idx < ((getLists.data.lists ?? []).length - 1) && <ListSeparator className={st.ListSeparator} />}
            </React.Fragment>
          );
        })}
      </div>
    );
  }, [getLists.data?.lists]);

  if (getFace.isPending) return null;

  return (
    <div className={st.Root}>
      <h5 className={st.Header}>{t('face-page:general_info.header')}</h5>
      <div className={st.PhotoAndActions}>
        <FaceSample
          buttonOnBottom={(
            <ButtonScrimGroup size="small">
              <IconButton
                disabled={!avatarSrc}
                icon={<ZoomInIcon />}
                kind="secondary"
                onClick={fullscreenImageViewer}
                size="small"
                variant="flat"
              />
            </ButtonScrimGroup>
          )}
          className={st.FaceSample}
          data-testid="sampleImage"
          src={avatarSrc}
        />
        <div className={st.Actions}>
          {!!canSearchFace && <SearchSimilarFacesButtonPoint3 faceId={faceId} />}
          {!!canUpdateCard && <EditFaceInfoButtonPoint3 faceId={faceId} />}
          {!!canDeleteCard && <DeleteFaceButtonPoint3 faceId={faceId} />}
        </div>
      </div>
      <div className={st.Data}>
        <div className={st.DataItem}>
          <span className={st.DataLabel}>{t('face-page:general_info.data.create_time')}</span>
          <span className={st.DataValue_text}>{fmt(parseISOToLocale(getFace.data?.create_time))}</span>
        </div>
        <div className={st.DataDiv} />
        <div className={st.DataItem}>
          <span className={st.DataLabel}>{t('face-page:general_info.data.external_id')}</span>
          <span className={st.DataValue_text}>{fmt(externalIdLink)}</span>
        </div>
        <div className={st.DataDiv} />
        {!!canReadLists && (
        <div className={st.DataItem}>
          <span className={st.DataLabel}>{t('face-page:general_info.data.lists')}</span>
          <span className={st.DataValue_text}>{fmt(lists)}</span>
        </div>
        )}
        <div className={st.DataDiv} />
        <div className={st.DataItem}>
          <span className={st.DataLabel}>{t('face-page:general_info.data.user_data')}</span>
          <span className={st.DataValue_text}>{fmt(getFace.data?.user_data)}</span>
        </div>
        <div className={st.DataDiv} />
        <div className={st.DataItem}>
          <span className={st.DataLabel}>{t('face-page:general_info.data.face_id')}</span>
          <span className={st.CopyFaceId}>
            <TruncatedText
              hasTruncation
              tooltipContent={(getFace.data as OutputFace).face_id}
            >
              {(getFace.data as OutputFace).face_id}
            </TruncatedText>
            <Tooltip
              position="bottom"
              tooltipContent={t('копировать')}
            >
              <CopyButton
                className={st.CopyButton}
                round
                value={(getFace.data as OutputFace).face_id as FaceId}
              />
            </Tooltip>
          </span>
        </div>
        <div className={st.DataDiv} />
      </div>
    </div>
  );
}

export { FaceGeneralInfo };
